import React, { lazy, Suspense } from "react";
import { Routes, Route, Navigate, useSearchParams } from "react-router-dom";
import { AuthProvider } from "./context/UnifiedAuthContext";
import PushNotificationSetup from "./components/PushNotificationSetup";
import AppUpdateModal from "./components/AppUpdateModal";
import BottomTabBar from "./components/BottomTabBar";
import PageTransition from "./components/PageTransition";
import { useAppUpdate } from "./hooks/useAppUpdate";
import { useStatusBar } from "./hooks/useStatusBar";
import { useNativeKeyboard } from "./hooks/useNativeKeyboard";
import { useSwipeBack } from "./hooks/useSwipeBack";
import { useRouteRestore } from "./hooks/useRouteRestore";
import { ChevronLeft } from "lucide-react";
import './App.css';
import './css/print.css';

// Lazy-loaded page components (route-based code splitting)
const LeaderboardPage = lazy(() => import("./pages/LeaderboardPage"));
const QuotasPage = lazy(() => import("./pages/QuotasPage"));
const QuotaSimulatorPage = lazy(() => import("./pages/QuotaSimulatorPage"));
const WeatherPage = lazy(() => import("./pages/WeatherPage"));
const EventsPage = lazy(() => import("./pages/EventsPage"));
const RecapsPage = lazy(() => import("./pages/RecapsPage"));
const RecapDetail = lazy(() => import("./pages/RecapDetail"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const AddEventPage = lazy(() => import("./pages/AddEventPage"));
const AddPlayerPage = lazy(() => import("./pages/AddPlayerPage"));
const AddCourse = lazy(() => import("./pages/AddCoursePage"));
const Rules = lazy(() => import("./pages/RulesPage"));
const PlayoffRules = lazy(() => import("./pages/PlayoffRulesPage"));
const PollPage = lazy(() => import("./pages/PollPage"));
const PrintScorecardPage = lazy(() => import("./pages/PrintScorecardPage"));
const FantasyGolf = lazy(() => import("./pages/FantasyGolf"));
const FantasyLeaderboard = lazy(() => import("./pages/FantasyLeaderboard"));
const MailingListPage = lazy(() => import("./pages/MailingListPage"));
const GalleryPage = lazy(() => import("./pages/GalleryPage"));
const SuggestionsPage = lazy(() => import("./pages/SuggestionsPage"));
const MagicLinkVerification = lazy(() => import("./pages/MagicLinkVerification"));
const AuthSuccess = lazy(() => import("./pages/AuthSuccess"));
const AuthError = lazy(() => import("./pages/AuthError"));
const LiveScoringPage = lazy(() => import("./pages/LiveScoringPage"));
const LiveLeaderboardPage = lazy(() => import("./pages/LiveLeaderboardPage"));
const LiveChatPage = lazy(() => import("./pages/LiveChatPage"));
const LiveDisplayPage = lazy(() => import("./pages/LiveDisplayPage"));
const AdminLiveScoringPage = lazy(() => import("./pages/AdminLiveScoringPage"));
const AdminPairingsPage = lazy(() => import("./pages/AdminPairingsPage"));
const RoleManagementPage = lazy(() => import("./pages/RoleManagementPage"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminBotSettingsPage = lazy(() => import("./pages/AdminBotSettingsPage"));
const AdminPotPaymentPage = lazy(() => import("./pages/AdminPotPaymentPage"));
const AdminPlayoffsPage = lazy(() => import("./pages/AdminPlayoffsPage"));
const AdminEventArchivePage = lazy(() => import("./pages/AdminEventArchivePage"));

// Wrapper to force LiveScoringPage remount when groupNumber changes
function LiveScoringWrapper() {
  const [searchParams] = useSearchParams();
  const groupNumber = searchParams.get('groupNumber') || 'none';
  // Key forces full remount when groupNumber changes
  return <LiveScoringPage key={`live-scoring-${groupNumber}`} />;
}

function AppUpdateCheck() {
  const { showModal, forceUpdate, latestVersion, dismissUpdate } = useAppUpdate();
  if (!showModal) return null;
  return (
    <AppUpdateModal
      forceUpdate={forceUpdate}
      latestVersion={latestVersion}
      onDismiss={dismissUpdate}
    />
  );
}

function SwipeBackIndicator() {
  const { swipeX, isSwiping } = useSwipeBack();
  if (!isSwiping) return null;
  const opacity = Math.min(swipeX / 80, 1);
  return (
    <div
      className="fixed top-1/2 -translate-y-1/2 z-50 pointer-events-none"
      style={{ left: Math.max(swipeX - 30, 8), opacity }}
    >
      <div className="w-10 h-10 rounded-full bg-black/20 dark:bg-white/20 backdrop-blur flex items-center justify-center">
        <ChevronLeft className="w-5 h-5 text-white dark:text-gray-200" />
      </div>
    </div>
  );
}

function AppShell() {
  useStatusBar();
  useNativeKeyboard();
  useRouteRestore();

  return (
    <>
      <AppUpdateCheck />
      <SwipeBackIndicator />
      <Suspense fallback={null}>
      <PageTransition>
        <Routes>
          <Route path="/" element={<LeaderboardPage />} />
          <Route path="/leaderboard" element={<LeaderboardPage />} />
          <Route path="/quotas" element={<QuotasPage />} />
          <Route path="/quota-simulator" element={<QuotaSimulatorPage />} />
          <Route path="/weather" element={<WeatherPage />} />
          <Route path="/events" element={<EventsPage />} />
          <Route path="/recaps" element={<RecapsPage />} />
          <Route path="/recaps/:id" element={<RecapDetail />} />
          <Route path="/fantasy-golf" element={<FantasyGolf />} />
          <Route path="/fantasy-leaderboard" element={<FantasyLeaderboard />} />
          <Route path="/fantasy" element={<Navigate to="/fantasy-golf" replace />} />
          <Route path="/gallery" element={<GalleryPage />} />
          <Route path="/suggestions" element={<SuggestionsPage />} />
          <Route path="/live" element={<LiveScoringWrapper />} />
          <Route path="/live/leaderboard" element={<LiveLeaderboardPage />} />
          <Route path="/live/display" element={<LiveDisplayPage />} />
          <Route path="/live/chat" element={<LiveChatPage />} />
          <Route path="/auth/magic" element={<MagicLinkVerification />} />
          <Route path="/auth/verify" element={<MagicLinkVerification />} />
          <Route path="/auth/success" element={<AuthSuccess />} />
          <Route path="/auth/error" element={<AuthError />} />
          <Route path="/admin/record-results" element={<AdminPage />} />
          <Route path="/admin/live-scoring" element={<AdminLiveScoringPage />} />
          <Route path="/admin/pairings" element={<AdminPairingsPage />} />
          <Route path="/admin/manage-event" element={<AddEventPage />} />
          <Route path="/admin/manage-course" element={<AddCourse />} />
          <Route path="/admin/manage-players" element={<AddPlayerPage />} />
          <Route path="/admin/mailing-list" element={<MailingListPage />} />
          <Route path="/admin/roles" element={<RoleManagementPage />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
          <Route path="/admin/bot" element={<AdminBotSettingsPage />} />
          <Route path="/admin/payouts" element={<AdminPotPaymentPage />} />
          <Route path="/admin/playoffs" element={<AdminPlayoffsPage />} />
          <Route path="/admin/events/:eventId/archive" element={<AdminEventArchivePage />} />
          <Route path="/rules" element={<Rules />} />
          <Route path="/rules/playoffs" element={<PlayoffRules />} />
          <Route path="/printScorecard" element={<PrintScorecardPage />} />
          <Route path="/poll" element={<PollPage />} />
          <Route path="/recaps" element={<RecapsPage />} />
          <Route path="/recaps/:recapId" element={<RecapDetail />} />
        </Routes>
      </PageTransition>
      </Suspense>
      <BottomTabBar />
      <PushNotificationSetup />
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppShell />
    </AuthProvider>
  );
}

export default App;
